import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom, map, Observable } from 'rxjs';
import { SubscriptionStatus } from 'src/app/models/payment';
import { Institution } from 'src/app/models/user';
import { Collaboration } from '@pncl/common-models';
import { combineLatest, startWith } from 'rxjs';
import {
  SplitScreenModalConfig,
  SplitScreenFormBasedComponent,
} from '../../standalones/components/modals/split-screen-modals/split-screen-form-based-modal/split-screen-form-based-modal.component';
import { PencilButtonThemeEnum } from '../../standalones/components/pencil-button/pencil-button.component';
import { IconTypes } from '../../standalones/components/pencil-icon/pencil-icon.component';
import { NAME_VALIDATORS } from '../../common/utils/form-validators';
import { PaymentPlan, ChargebeeSubscriptionData } from '../../models/payment';
import { UserService } from '../../services/user.service';
import { ActionsQueryParamKeys } from '../../common/utils/actions-query-params-util';
import { CustomerSupportService } from '../../services/customer-support.service';
import { HomeNavigationService } from '../../services/home-navigation.service';
import { NotificationToasterService } from '../../services/notification-toaster.service';
import {
  NotificationActionButton,
  NotificationActionButtonsConfig,
} from '../../services/notification-toaster.service';
import { TOAST_CLASSES } from '../../common/utils/notification-constants';
import { SVGIconComponentsModule } from '../../common/svg-icon/svg-icon-components.module';
import { buildPaywallConfigFromPaymentPlan } from '../../utilities/payment-plan-paywall.utils';

interface InstitutionForm {
  name: FormControl<string | null>;
}

@Component({
  selector: 'app-institution-details-modal',
  templateUrl: './institution-details-modal.component.html',
  styleUrls: ['./institution-details-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    SplitScreenFormBasedComponent,
    SVGIconComponentsModule,
  ],
})
export class InstitutionDetailsModalComponent {
  form: FormGroup<InstitutionForm>;
  readonly isLoading$ = new BehaviorSubject<boolean>(false);
  readonly MAX_ENTITY_TITLE_LENGTH = Collaboration.Constants.MAX_ENTITY_TITLE_LENGTH;

  // Expose enum for template use
  PencilButtonThemeEnum = PencilButtonThemeEnum;
  IconTypes = IconTypes;

  // Modal data passed from the service
  @Input() chargebeeSubscriptionData!: ChargebeeSubscriptionData;
  @Input() plan!: Partial<PaymentPlan>;
  @Output() closeFullScreenModal = new EventEmitter<any>();

  // Core form configuration
  readonly formConfig$: Observable<SplitScreenModalConfig>;
  private readonly isFormInvalid$: Observable<boolean>;

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private translateService: TranslateService,
    private notificationToasterService: NotificationToasterService,
    private readonly customerSupportService: CustomerSupportService,
    private readonly homeNavigation: HomeNavigationService,
  ) {
    this.form = this.formBuilder.group({
      name: ['', NAME_VALIDATORS],
    });

    this.isFormInvalid$ = this.form.statusChanges.pipe(
      map(() => this.form.invalid),
      startWith(this.form.invalid),
    );

    this.formConfig$ = combineLatest([this.isFormInvalid$, this.isLoading$]).pipe(
      map(
        ([isFormInvalid, isLoading]): SplitScreenModalConfig => ({
          submitButtonText: this.translateService.instant('Done'),
          isSubmitDisabled: isFormInvalid,
          isLoading,
          showPreview: true,
          fullScreen: true,
          formLoadingText: this.translateService.instant('Setting up your institution...'),
          formLoadingSubtext: this.translateService.instant('This may take a few moments'),
        }),
      ),
    );
  }

  async onSubmit(): Promise<void> {
    if (this.form.valid) {
      this.isLoading$.next(true);
      const formValues = this.form.getRawValue();

      try {
        const institution: Institution = {
          name: formValues.name ?? '',
          logo: '', // Submit empty string, backend will handle default
          email: this.chargebeeSubscriptionData.chargebeeEmail,
          phone: '',
          description: '',
          location: [],
          tierConfiguration: {
            tier: this.plan.tier ?? '',
          },
          paywallConfig: buildPaywallConfigFromPaymentPlan(this.plan),
          billingStatus: {
            subscription_status: SubscriptionStatus.ACTIVE,
            customerId: this.chargebeeSubscriptionData.customerId,
            subscription: {
              plan: this.chargebeeSubscriptionData.planId,
              chargebeeId: this.chargebeeSubscriptionData.subscriptionId,
              email: this.chargebeeSubscriptionData.chargebeeEmail,
            },
          },
        };

        await firstValueFrom(this.userService.upgradeToInstitution(institution));
        this.homeNavigation.hardRedirect({
          [ActionsQueryParamKeys.POST_INSTITUTION_UPGRADE]: 'true',
        });
      } catch (error) {
        console.error('Error creating institution:', error);
        this.showInstitutionSetupErrorNotification();
      }
    }
  }

  private showInstitutionSetupErrorNotification(): void {
    const refreshButton: NotificationActionButton = {
      text: this.translateService.instant('Refresh Page'),
      onClick: () => {
        window.location.reload();
      },
      close: true,
      icon: 'refresh',
      iconConfig: {
        type: IconTypes.matIcon,
      },
    };

    const supportButton: NotificationActionButton = {
      text: this.translateService.instant('Contact Support'),
      onClick: () => {
        // Open support link in new tab
        this.customerSupportService.openSupport();
      },
      icon: 'help_outline',
      iconConfig: {
        type: IconTypes.matIcon,
      },
    };

    const buttons: NotificationActionButtonsConfig = {
      primary: refreshButton,
      secondary: supportButton,
    };

    this.notificationToasterService.showDefaultErrorNotificationV2(
      this.translateService.instant('Institution setup failed'),
      this.translateService.instant(
        'We encountered an issue while setting up your institution. Please contact support for assistance with your upgrade.',
      ),
      this.notificationToasterService.generateNotificationCode(),
      undefined, // Use default error icon
      buttons,
      0, // Use default timeout
      undefined, // Use default onActivateTick
      () => {}, // Default onTimedOut
      TOAST_CLASSES.ULTRA_HIGH_Z_INDEX,
    );
  }

  // Getter for institution name to use in preview
  get institutionName(): string {
    return this.form.get('name')?.value || '';
  }
}
